/* eslint-disable @typescript-eslint/naming-convention */
import type {
  GraphQLSupporterGiftLimitItem,
  GraphQLSupporterGiftLimits,
  GraphQLSupporterGiftTypeLimits,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';

const mockPaymentOptions = ({
  oneTime,
  monthly = false,
  quarterly = false,
  semiAnnual = false,
  annual = false,
}: {
  oneTime: boolean;
  monthly?: boolean;
  quarterly?: boolean;
  semiAnnual?: boolean;
  annual?: boolean;
}) => ({ oneTime, monthly, quarterly, semiAnnual, annual });

type MockPaymentOptionsMinMax = Pick<
  GraphQLSupporterGiftLimitItem['paymentOptionsMinMax'],
  'oneTime'
> &
  Partial<GraphQLSupporterGiftLimitItem['paymentOptionsMinMax']>;

const mockPaymentOptionsMinMax = ({
  oneTime,
  monthly = { minAmount: 0, maxAmount: 0 },
  quarterly = { minAmount: 0, maxAmount: 0 },
  semiAnnual = { minAmount: 0, maxAmount: 0 },
  annual = { minAmount: 0, maxAmount: 0 },
}: MockPaymentOptionsMinMax) => ({
  oneTime,
  monthly,
  quarterly,
  semiAnnual,
  annual,
});

const mockGiftLimitItem = ({
  remainingAmount,
  remainingQuantity,
  ineligibilityCode = '',
  paymentOptions,
  paymentOptionsMinMax,
}: GraphQLSupporterGiftLimitItem): GraphQLSupporterGiftLimitItem => ({
  remainingAmount,
  remainingQuantity,
  ineligibilityCode,
  paymentOptions,
  paymentOptionsMinMax,
});

const mockGiftTypeLimits = (
  giftTypes: Partial<GraphQLSupporterGiftTypeLimits>,
): GraphQLSupporterGiftTypeLimits => ({
  Birthday: null,
  Child: null,
  Family: null,
  Center: null,
  Final: null,
  ...giftTypes,
});

const mockBirthdayLimit = ({
  remainingAmount = 150,
  remainingQuantity = 1,
  ineligibilityCode = '',
  oneTime = true,
  annual = true,
}: {
  remainingAmount?: number;
  remainingQuantity?: number;
  ineligibilityCode?: string;
  oneTime?: boolean;
  annual?: boolean;
} = {}) =>
  mockGiftLimitItem({
    remainingAmount,
    remainingQuantity,
    ineligibilityCode,
    paymentOptions: mockPaymentOptions({ oneTime, annual }),
    paymentOptionsMinMax: mockPaymentOptionsMinMax({
      oneTime: { minAmount: 10, maxAmount: 150 },
      annual: { minAmount: 10, maxAmount: 150 },
    }),
  });

const mockChildLimit = ({
  remainingQuantity = 1,
  annual = true,
}: {
  remainingQuantity?: number;
  annual?: boolean;
} = {}) =>
  mockGiftLimitItem({
    remainingAmount: 150,
    remainingQuantity,
    ineligibilityCode: '',
    paymentOptions: mockPaymentOptions({ oneTime: true, annual }),
    paymentOptionsMinMax: mockPaymentOptionsMinMax({
      oneTime: { minAmount: 10, maxAmount: 150 },
      annual: { minAmount: 10, maxAmount: 150 },
    }),
  });

const mockFamilyLimit = ({
  remainingAmount,
  remainingQuantity,
  recurringAvailable,
}: {
  remainingAmount: number;
  remainingQuantity: number;
  recurringAvailable: boolean;
}) =>
  mockGiftLimitItem({
    remainingAmount,
    remainingQuantity,
    ineligibilityCode: '',
    paymentOptions: mockPaymentOptions({
      oneTime: true,
      monthly: recurringAvailable,
      quarterly: recurringAvailable,
      semiAnnual: recurringAvailable,
      annual: recurringAvailable,
    }),
    paymentOptionsMinMax: mockPaymentOptionsMinMax({
      oneTime: { minAmount: 25, maxAmount: 1100 },
      monthly: { minAmount: 25, maxAmount: 91 },
      quarterly: { minAmount: 25, maxAmount: 275 },
      semiAnnual: { minAmount: 25, maxAmount: 550 },
      annual: { minAmount: 25, maxAmount: 1100 },
    }),
  });

const mockFinalLimit = ({
  remainingAmount = 3000,
  remainingQuantity = 1,
  ineligibilityCode = '',
  oneTime = true,
}: {
  remainingAmount?: number;
  remainingQuantity?: number;
  ineligibilityCode?: string;
  oneTime?: boolean;
} = {}) =>
  mockGiftLimitItem({
    remainingAmount,
    remainingQuantity,
    ineligibilityCode,
    paymentOptions: mockPaymentOptions({ oneTime }),
    paymentOptionsMinMax: mockPaymentOptionsMinMax({
      oneTime: { minAmount: 10, maxAmount: 3000 },
    }),
  });

const mockCenterLimit = ({
  remainingAmount,
  remainingQuantity,
}: {
  remainingAmount: number;
  remainingQuantity: number;
}) =>
  mockGiftLimitItem({
    remainingAmount,
    remainingQuantity,
    ineligibilityCode: '',
    paymentOptions: mockPaymentOptions({
      oneTime: true,
      quarterly: true,
      semiAnnual: true,
      annual: true,
    }),
    paymentOptionsMinMax: mockPaymentOptionsMinMax({
      oneTime: { minAmount: 50, maxAmount: 15_000 },
      quarterly: { minAmount: 50, maxAmount: 3750 },
      semiAnnual: { minAmount: 50, maxAmount: 7500 },
      annual: { minAmount: 50, maxAmount: 15_000 },
    }),
  });

export const MOCK_FAMILY_GIFT_LIMIT_PARTIALLY_GIVEN = mockFamilyLimit({
  remainingAmount: 500,
  remainingQuantity: 1000,
  recurringAvailable: true,
});

export const MOCK_SUPPORTER_GIFT_LIMITS: GraphQLSupporterGiftLimits = {
  children: [
    {
      centerId: 'GH0422',
      id: '05518723',
      giftTypes: {
        Birthday: {
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'BG-Gift-Already-Given',
          paymentOptions: {
            oneTime: false,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: false,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Child: {
          remainingAmount: 150,
          remainingQuantity: 1,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Family: {
          remainingAmount: 1100,
          remainingQuantity: 999,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: true,
            quarterly: true,
            semiAnnual: true,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 1100,
            },
            monthly: {
              minAmount: 25,
              maxAmount: 91,
            },
            quarterly: {
              minAmount: 25,
              maxAmount: 275,
            },
            semiAnnual: {
              minAmount: 25,
              maxAmount: 550,
            },
            annual: {
              minAmount: 25,
              maxAmount: 1100,
            },
          },
        },
        Center: null,
        Final: {
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Not-Available',
          paymentOptions: {
            oneTime: false,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: false,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 3000,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 0,
              maxAmount: 0,
            },
          },
        },
      },
    },
    {
      centerId: 'KE0219',
      id: '09539286',
      giftTypes: {
        Birthday: {
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'BG-Gift-Already-Given',
          paymentOptions: {
            oneTime: false,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: false,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Child: {
          remainingAmount: 150,
          remainingQuantity: 1,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Family: {
          remainingAmount: 1100,
          remainingQuantity: 999,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: true,
            quarterly: true,
            semiAnnual: true,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 1100,
            },
            monthly: {
              minAmount: 25,
              maxAmount: 91,
            },
            quarterly: {
              minAmount: 25,
              maxAmount: 275,
            },
            semiAnnual: {
              minAmount: 25,
              maxAmount: 550,
            },
            annual: {
              minAmount: 25,
              maxAmount: 1100,
            },
          },
        },
        Center: null,
        Final: {
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Not-Available',
          paymentOptions: {
            oneTime: false,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: false,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 3000,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 0,
              maxAmount: 0,
            },
          },
        },
      },
    },
    {
      centerId: 'RW0677',
      id: '11760746',
      giftTypes: {
        Birthday: {
          remainingAmount: 150,
          remainingQuantity: 1,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Child: {
          remainingAmount: 150,
          remainingQuantity: 2,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Family: {
          remainingAmount: 1100,
          remainingQuantity: 999,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: true,
            quarterly: true,
            semiAnnual: true,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 1100,
            },
            monthly: {
              minAmount: 25,
              maxAmount: 91,
            },
            quarterly: {
              minAmount: 25,
              maxAmount: 275,
            },
            semiAnnual: {
              minAmount: 25,
              maxAmount: 550,
            },
            annual: {
              minAmount: 25,
              maxAmount: 1100,
            },
          },
        },
        Center: null,
        Final: {
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Not-Available',
          paymentOptions: {
            oneTime: false,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: false,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 3000,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 0,
              maxAmount: 0,
            },
          },
        },
      },
    },
    {
      centerId: 'HA0402',
      id: '11777857',
      giftTypes: {
        Birthday: {
          remainingAmount: 150,
          remainingQuantity: 1,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Child: {
          remainingAmount: 150,
          remainingQuantity: 2,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Family: {
          remainingAmount: 1100,
          remainingQuantity: 999,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: true,
            quarterly: true,
            semiAnnual: true,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 1100,
            },
            monthly: {
              minAmount: 25,
              maxAmount: 91,
            },
            quarterly: {
              minAmount: 25,
              maxAmount: 275,
            },
            semiAnnual: {
              minAmount: 25,
              maxAmount: 550,
            },
            annual: {
              minAmount: 25,
              maxAmount: 1100,
            },
          },
        },
        Center: null,
        Final: {
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Not-Available',
          paymentOptions: {
            oneTime: false,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: false,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 3000,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 0,
              maxAmount: 0,
            },
          },
        },
      },
    },
    {
      centerId: 'ET0782',
      id: '11807311',
      giftTypes: {
        Birthday: {
          remainingAmount: 150,
          remainingQuantity: 1,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Child: {
          remainingAmount: 150,
          remainingQuantity: 2,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Family: {
          remainingAmount: 1100,
          remainingQuantity: 999,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: true,
            quarterly: true,
            semiAnnual: true,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 1100,
            },
            monthly: {
              minAmount: 25,
              maxAmount: 91,
            },
            quarterly: {
              minAmount: 25,
              maxAmount: 275,
            },
            semiAnnual: {
              minAmount: 25,
              maxAmount: 550,
            },
            annual: {
              minAmount: 25,
              maxAmount: 1100,
            },
          },
        },
        Center: null,
        Final: {
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Not-Available',
          paymentOptions: {
            oneTime: false,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: false,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 3000,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 0,
              maxAmount: 0,
            },
          },
        },
      },
    },
    {
      centerId: 'ET0782',
      id: '11807437',
      giftTypes: {
        Birthday: {
          remainingAmount: 150,
          remainingQuantity: 1,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Child: {
          remainingAmount: 150,
          remainingQuantity: 2,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 10,
              maxAmount: 150,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 10,
              maxAmount: 150,
            },
          },
        },
        Family: {
          remainingAmount: 1100,
          remainingQuantity: 999,
          ineligibilityCode: '',
          paymentOptions: {
            oneTime: true,
            monthly: true,
            quarterly: true,
            semiAnnual: true,
            annual: true,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 1100,
            },
            monthly: {
              minAmount: 25,
              maxAmount: 91,
            },
            quarterly: {
              minAmount: 25,
              maxAmount: 275,
            },
            semiAnnual: {
              minAmount: 25,
              maxAmount: 550,
            },
            annual: {
              minAmount: 25,
              maxAmount: 1100,
            },
          },
        },
        Center: null,
        Final: {
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Not-Available',
          paymentOptions: {
            oneTime: false,
            monthly: false,
            quarterly: false,
            semiAnnual: false,
            annual: false,
          },
          paymentOptionsMinMax: {
            oneTime: {
              minAmount: 25,
              maxAmount: 3000,
            },
            monthly: {
              minAmount: 0,
              maxAmount: 0,
            },
            quarterly: {
              minAmount: 0,
              maxAmount: 0,
            },
            semiAnnual: {
              minAmount: 0,
              maxAmount: 0,
            },
            annual: {
              minAmount: 0,
              maxAmount: 0,
            },
          },
        },
      },
    },
    {
      id: '08314403',
      giftTypes: mockGiftTypeLimits({
        Final: mockFinalLimit(),
      }),
    },
    {
      id: '11837947',
      giftTypes: mockGiftTypeLimits({
        Birthday: mockBirthdayLimit({
          remainingAmount: 0,
          remainingQuantity: 0,
          oneTime: false,
        }),
        Child: mockChildLimit(),
        Family: mockFamilyLimit({
          remainingAmount: 600,
          remainingQuantity: 998,
          recurringAvailable: true,
        }),
        Final: mockFinalLimit({
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Not-Available',
          oneTime: false,
        }),
      }),
    },
    {
      id: '08702636',
      giftTypes: mockGiftTypeLimits({
        Birthday: mockBirthdayLimit(),
        Child: mockChildLimit(),
        Family: mockFamilyLimit({
          remainingAmount: 530,
          remainingQuantity: 997,
          recurringAvailable: false,
        }),
        Final: mockFinalLimit(),
      }),
    },
    {
      id: '05463400',
      giftTypes: mockGiftTypeLimits({
        Birthday: mockBirthdayLimit({
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'BG-Gift-Already-Given',
          oneTime: false,
          annual: false,
        }),
        Child: mockChildLimit(),
        Family: mockFamilyLimit({
          remainingAmount: 530,
          remainingQuantity: 997,
          recurringAvailable: false,
        }),
        Final: mockFinalLimit({
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Not-Available',
          oneTime: false,
        }),
      }),
    },
    {
      id: '12337946',
      giftTypes: mockGiftTypeLimits({
        Birthday: mockBirthdayLimit({
          remainingAmount: 0,
          remainingQuantity: 0,
          oneTime: false,
          annual: false,
        }),
        Child: mockChildLimit(),
        Family: mockFamilyLimit({
          remainingAmount: 600,
          remainingQuantity: 998,
          recurringAvailable: true,
        }),
        Final: mockFinalLimit({
          remainingAmount: 0,
          remainingQuantity: 0,
          ineligibilityCode: 'FLG-Gift-Already-Given',
          oneTime: false,
        }),
      }),
    },
  ],
  projects: [
    {
      id: 'GH3012',
      giftTypes: mockGiftTypeLimits({
        Center: mockCenterLimit({
          remainingAmount: 6100,
          remainingQuantity: 1,
        }),
      }),
    },
    {
      id: 'GH1234',
      giftTypes: mockGiftTypeLimits({
        Center: mockCenterLimit({
          remainingAmount: 15_000,
          remainingQuantity: 4,
        }),
      }),
    },
  ],
  defaults: {
    Birthday: {
      remainingAmount: 150,
      remainingQuantity: 1,
      ineligibilityCode: '',
      paymentOptions: {
        oneTime: true,
        monthly: false,
        quarterly: false,
        semiAnnual: false,
        annual: true,
      },
      paymentOptionsMinMax: {
        oneTime: {
          minAmount: 10,
          maxAmount: 150,
        },
        monthly: {
          minAmount: 0,
          maxAmount: 0,
        },
        quarterly: {
          minAmount: 0,
          maxAmount: 0,
        },
        semiAnnual: {
          minAmount: 0,
          maxAmount: 0,
        },
        annual: {
          minAmount: 10,
          maxAmount: 150,
        },
      },
    },
    Child: {
      remainingAmount: 150,
      remainingQuantity: 2,
      ineligibilityCode: '',
      paymentOptions: {
        oneTime: true,
        monthly: false,
        quarterly: false,
        semiAnnual: false,
        annual: true,
      },
      paymentOptionsMinMax: {
        oneTime: {
          minAmount: 10,
          maxAmount: 150,
        },
        monthly: {
          minAmount: 0,
          maxAmount: 0,
        },
        quarterly: {
          minAmount: 0,
          maxAmount: 0,
        },
        semiAnnual: {
          minAmount: 0,
          maxAmount: 0,
        },
        annual: {
          minAmount: 10,
          maxAmount: 150,
        },
      },
    },
    Family: {
      remainingAmount: 1100,
      remainingQuantity: 999,
      ineligibilityCode: '',
      paymentOptions: {
        oneTime: true,
        monthly: true,
        quarterly: true,
        semiAnnual: true,
        annual: true,
      },
      paymentOptionsMinMax: {
        oneTime: {
          minAmount: 25,
          maxAmount: 1100,
        },
        monthly: {
          minAmount: 25,
          maxAmount: 91,
        },
        quarterly: {
          minAmount: 25,
          maxAmount: 275,
        },
        semiAnnual: {
          minAmount: 25,
          maxAmount: 550,
        },
        annual: {
          minAmount: 25,
          maxAmount: 1100,
        },
      },
    },
    Center: {
      remainingAmount: 15_000,
      remainingQuantity: 4,
      ineligibilityCode: '',
      paymentOptions: {
        oneTime: true,
        monthly: false,
        quarterly: true,
        semiAnnual: true,
        annual: true,
      },
      paymentOptionsMinMax: {
        oneTime: {
          minAmount: 50,
          maxAmount: 15_000,
        },
        monthly: {
          minAmount: 0,
          maxAmount: 0,
        },
        quarterly: {
          minAmount: 50,
          maxAmount: 3750,
        },
        semiAnnual: {
          minAmount: 50,
          maxAmount: 7500,
        },
        annual: {
          minAmount: 50,
          maxAmount: 15_000,
        },
      },
    },
    Final: {
      remainingAmount: 3000,
      remainingQuantity: 1,
      ineligibilityCode: '',
      paymentOptions: {
        oneTime: true,
        monthly: false,
        quarterly: false,
        semiAnnual: false,
        annual: false,
      },
      paymentOptionsMinMax: {
        oneTime: {
          minAmount: 25,
          maxAmount: 3000,
        },
        monthly: {
          minAmount: 0,
          maxAmount: 0,
        },
        quarterly: {
          minAmount: 0,
          maxAmount: 0,
        },
        semiAnnual: {
          minAmount: 0,
          maxAmount: 0,
        },
        annual: {
          minAmount: 0,
          maxAmount: 0,
        },
      },
    },
  },
};
